import { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
// @material-ui/core components
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";

import componentStylesGeneric from "assets/theme/views/admin/generic.js";
import LocationHeader from "components/Headers/LocationHeader";
import useFilterSync from "hooks/useFilterSync";
import { getFilteredOpportunitiesMap } from "mappers/OpportunityMap";
import {
    getFilteredOpportunitiesAction,
    getIsOpportunityApplicationAction,
} from "redux/actions/opportunitiesAction";
import { DATASET, PAGINATION } from "utils/const";
import { getDatasetStateByName } from "utils/datasets";
import FilterSection from "./Sections/OpportunitiesFilterSection";
import ResultSection from "./Sections/OpportunitiesResultSection";

const useStylesGeneric = makeStyles(componentStylesGeneric);

function OpportunitiesListInner(props) {
  const location = useLocation();
  const { setValue, getValues } = useFormContext();
  const [initialized, setInitialized] = useState(false);
  const readyRef = useRef(false);
  const submittedFilterRef = useRef(null);

  const provincesOptions = getDatasetStateByName(props, DATASET.PROVINCES);
  const opportunityTypeOptions = getDatasetStateByName(props, DATASET.OPPORTUNITY_TYPE);
  const opportunityStateOptions = getDatasetStateByName(props, DATASET.OPPORTUNITY_STATE);
  const professionalCategoryOptions = getDatasetStateByName(props, DATASET.PROFESSIONAL_CATEGORY);

  const findOptionByCode = (options, code) => {
    if (!options || options.length === 0 || code === null || code === undefined || code === "") return null;
    const codeStr = String(code);
    return options.find((o) => String(o?.code) === codeStr) || null;
  };

  const buildPayload = (values) => {
    const effectivePageIndex = values?.pageIndex ?? PAGINATION.FIRST_PAGE;
    const effectivePageSize = values?.pageSize ?? PAGINATION.PAGE_SIZE;
    return {
      ...getFilteredOpportunitiesMap(values),
      pageIndex: effectivePageIndex,
      pageSize: effectivePageSize,
    };
  };

  const fieldSerializers = useMemo(
    () => ({
      province: {
        toUrl: (opt) => opt?.code ?? null,
        fromUrl: (code) => findOptionByCode(provincesOptions, code),
      },
      opportunityType: {
        toUrl: (opt) => opt?.code ?? null,
        fromUrl: (code) => findOptionByCode(opportunityTypeOptions, code),
      },
      opportunityState: {
        toUrl: (opt) => opt?.code ?? null,
        fromUrl: (code) => findOptionByCode(opportunityStateOptions, code),
      },
      professionalCategory: {
        toUrl: (opt) => opt?.code ?? null,
        fromUrl: (code) => findOptionByCode(professionalCategoryOptions, code),
      },
    }),
    [
      provincesOptions,
      opportunityTypeOptions,
      opportunityStateOptions,
      professionalCategoryOptions,
    ]
  );

  const runSearch = (values) => {
    const payload = buildPayload(values);
    submittedFilterRef.current = payload;
    props.getFilteredOpportunities(payload);
  };

  const filterSync = useFilterSync({
    defaultValues: props.defaultValues,
    syncPagination: true,
    fieldSerializers,
    onFilterChange: (values) => {
      if (!readyRef.current) return;
      runSearch(values);
    },
  });

  useEffect(() => {
    if (initialized) return;

    const hasUrlParams = (location.search || "").length > 1;

    const requiresOptions = hasUrlParams;
    const optionsReady =
      !requiresOptions ||
      (provincesOptions.length > 0 &&
        opportunityTypeOptions.length > 0 &&
        opportunityStateOptions.length > 0 &&
        professionalCategoryOptions.length > 0);

    if (requiresOptions && !optionsReady) return;

    readyRef.current = false;
    if (hasUrlParams) {
      filterSync.syncFiltersFromUrl();
    }
    readyRef.current = true;

    runSearch(getValues());
    setInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialized,
    location.search,
    provincesOptions.length,
    opportunityTypeOptions.length,
    opportunityStateOptions.length,
    professionalCategoryOptions.length,
  ]);

  const handleFilterSubmit = (data) => {
    const nextValues = {
      ...data,
      pageIndex: PAGINATION.FIRST_PAGE,
      pageSize: data.pageSize ?? PAGINATION.PAGE_SIZE,
    };

    submittedFilterRef.current = buildPayload(nextValues);
    setValue("pageIndex", PAGINATION.FIRST_PAGE);
    filterSync.handleFilterSubmit(nextValues);
  };

  const getFilterForExport = () => submittedFilterRef.current || buildPayload(getValues());

  const handleFilterReset = () => {
    submittedFilterRef.current = buildPayload(props.defaultValues);
    filterSync.handleFilterReset();
  };

  const handlePageChange = (nextPageIndex) => {
    const effectivePageSize = getValues("pageSize") ?? PAGINATION.PAGE_SIZE;
    const base = submittedFilterRef.current || buildPayload(getValues());
    const nextPayload = {
      ...base,
      pageIndex: nextPageIndex,
      pageSize: effectivePageSize,
    };

    submittedFilterRef.current = nextPayload;
    setValue("pageIndex", nextPageIndex);
    filterSync.updatePagination(nextPageIndex, effectivePageSize);
    props.getFilteredOpportunities(nextPayload);
  };

  return (
    <>
      <Grid item xs={12} md={12}>
        <FilterSection onSubmit={handleFilterSubmit} onReset={handleFilterReset} getFilterForExport={getFilterForExport} />
      </Grid>
      <Grid item xs={12} md={12}>
        <Box classes={{ root: props.classes.tableResultBoxRoot }}>
          <ResultSection onPageChange={handlePageChange} />
        </Box>
      </Grid>
    </>
  );
}

function OpportunitiesList(props) {
  const classes = useStylesGeneric();

  const defaultValues = {
    reference: "",
    opportunityType: null,
    province: null,
    professionalCategory: null,
    searchText: "",
    opportunityState: null,
    pageIndex: PAGINATION.FIRST_PAGE,
    pageSize: PAGINATION.PAGE_SIZE,
  };

  const form = useForm({
    mode: "onChange",
    defaultValues,
  });

  const setIsOpportunityApplication = () => {
    props.getIsOpportunityApplication(false);
  };

  useEffect(() => {
    setIsOpportunityApplication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid container classes={{ root: classes.containerRoot }}>
      <LocationHeader
        section="Emprego e Estágio Profissional"
      />
      <FormProvider {...form}>
        <OpportunitiesListInner
          {...props}
          classes={classes}
          defaultValues={defaultValues}
        />
      </FormProvider>
    </Grid>
  );
}

const mapStateToProps = (state) => ({ ...state });

const mapDispatchToProps = (dispatch) => ({
  getIsOpportunityApplication: (isOpportunityApplication) =>
    dispatch(getIsOpportunityApplicationAction(isOpportunityApplication)),
  getFilteredOpportunities: (data) => dispatch(getFilteredOpportunitiesAction(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OpportunitiesList);
